import { z } from 'zod';

/**
 * Kimlik/pasaport OCR cikti semasi.
 * Bu sema iki yerde birden kullanilir:
 *  1) Gemini `generationConfig.responseSchema` uretimi (bkz. ../gemini-schema.ts)
 *  2) Worker tarafinda modelden gelen JSON'un dogrulanmasi (modele ASLA guvenilmez)
 */

export const BELGE_TIPLERI = [
  'TC_KIMLIK_KARTI', // yeni cipli kimlik karti (TD1 MRZ)
  'NUFUS_CUZDANI', // eski tip nufus cuzdani (MRZ yok)
  'PASAPORT', // TD3 MRZ
  'IKAMET_IZNI', // yabanci ikamet izni
  'EHLIYET', // yedek belge, KBS icin tek basina yeterli degil
  'BILINMIYOR',
] as const;
export const belgeTipiSchema = z.enum(BELGE_TIPLERI);
export type BelgeTipi = z.infer<typeof belgeTipiSchema>;

export const cinsiyetSchema = z.enum(['E', 'K', 'BILINMIYOR']);
export type Cinsiyet = z.infer<typeof cinsiyetSchema>;

/** ISO 8601 tarih (YYYY-AA-GG). Model her tarihi bu formata cevirmekle yukumlu. */
export const isoTarihSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarih YYYY-AA-GG biçiminde olmalı');

/** Guven skoru dogrulanan alanlar (UI'da sari vurgu bu listeye gore yapilir). */
export const OCR_ALANLARI = [
  'tckn',
  'pasaportNo',
  'ad',
  'soyad',
  'anneAdi',
  'babaAdi',
  'dogumTarihi',
  'dogumYeri',
  'cinsiyet',
  'uyruk',
  'seriNo',
  'gecerlilikTarihi',
] as const;
export type OcrAlani = (typeof OCR_ALANLARI)[number];

const guven = z.number().min(0).max(1).nullable();

export const alanGuvenleriSchema = z.object({
  tckn: guven,
  pasaportNo: guven,
  ad: guven,
  soyad: guven,
  anneAdi: guven,
  babaAdi: guven,
  dogumTarihi: guven,
  dogumYeri: guven,
  cinsiyet: guven,
  uyruk: guven,
  seriNo: guven,
  gecerlilikTarihi: guven,
});
export type AlanGuvenleri = z.infer<typeof alanGuvenleriSchema>;

/**
 * Modelin dondurmesi gereken ham JSON.
 * TUM alanlar zorunlu ama nullable: model bir alani okuyamazsa anahtari
 * silmek yerine null birakir; boylece "okunamadi" ile "hic denemedi" ayrisir.
 */
export const identityOcrSchema = z.object({
  belgeTipi: belgeTipiSchema,
  tckn: z.string().nullable(),
  pasaportNo: z.string().nullable(),
  ad: z.string().nullable(),
  soyad: z.string().nullable(),
  anneAdi: z.string().nullable(),
  babaAdi: z.string().nullable(),
  dogumTarihi: isoTarihSchema.nullable(),
  dogumYeri: z.string().nullable(),
  cinsiyet: cinsiyetSchema,
  /** ISO 3166-1 alpha-3, ornek: TUR, DEU, GBR */
  uyruk: z.string().length(3).nullable(),
  seriNo: z.string().nullable(),
  gecerlilikTarihi: isoTarihSchema.nullable(),
  /** MRZ ham satirlari (TD1 icin 3x30, TD3 icin 2x44). Yoksa null. */
  mrz: z.array(z.string()).nullable(),
  alanGuvenleri: alanGuvenleriSchema,
  /** Modelin kendi uyarilari: "fotograf bulanik", "arka yuz eksik" vb. */
  uyarilar: z.array(z.string()),
});
export type IdentityOcr = z.infer<typeof identityOcrSchema>;

/** Worker tarafi dogrulama sonucu (Gemini'ye guvenilmeyen katman). */
export const dogrulamaSchema = z.object({
  tcknGecerli: z.boolean().nullable(),
  mrzGecerli: z.boolean().nullable(),
  /** MRZ ile gorsel alanlar arasinda uyusmayan alan adlari */
  mrzCakisanAlanlar: z.array(z.string()),
  /** dogum tarihi / gecerlilik tarihi mantik kontrolleri */
  tarihUyarilari: z.array(z.string()),
  /** UI'da sari isaretlenecek alanlar (dusuk guven veya tutarsizlik) */
  dikkatGerektirenAlanlar: z.array(z.string()),
  /** KBS bildirimi icin eksik olan zorunlu alanlar */
  kbsEksikAlanlar: z.array(z.string()),
  /**
   * Belgenin ARKA yuzu hala gerekli mi.
   *
   * Yeni cipli TC kimlik kartinda anne adi, baba adi ve dogum yeri ARKA
   * yuzdedir; anne/baba adi ise KBS'de ZORUNLUDUR. Yalnizca on yuz okutulursa
   * bu alanlar bos gelir ve gorevli elle yazmak zorunda kalir - kimlik
   * okumanin butun anlami kaybolur. Bu bayrak "arka yuzu de okut" demektir.
   */
  arkaYuzGerekli: z.boolean(),
  /** 18 yasindan kucuk mu (refakatci kurali icin) */
  resitDegil: z.boolean().nullable(),
});
export type Dogrulama = z.infer<typeof dogrulamaSchema>;

export const identityOcrResultSchema = z.object({
  alanlar: identityOcrSchema,
  dogrulama: dogrulamaSchema,
  meta: z.object({
    modelAdi: z.string(),
    sureMs: z.number(),
    denemeSayisi: z.number(),
    kotaKalan: z.number().nullable(),
    /** "kimlik kopyasi sakla" acikken R2 anahtari, aksi halde null */
    belgeR2Key: z.string().nullable(),
  }),
});
export type IdentityOcrResult = z.infer<typeof identityOcrResultSchema>;

/** Guven esigi: bunun altindaki alanlar UI'da vurgulanir, sessizce kabul edilmez. */
export const GUVEN_ESIGI = 0.85;

/** Bos (tumu null) OCR objesi - manuel giris fallback'i icin. */
export function bosIdentityOcr(): IdentityOcr {
  const guvenler = Object.fromEntries(OCR_ALANLARI.map((a) => [a, null])) as AlanGuvenleri;
  return {
    belgeTipi: 'BILINMIYOR',
    tckn: null,
    pasaportNo: null,
    ad: null,
    soyad: null,
    anneAdi: null,
    babaAdi: null,
    dogumTarihi: null,
    dogumYeri: null,
    cinsiyet: 'BILINMIYOR',
    uyruk: null,
    seriNo: null,
    gecerlilikTarihi: null,
    mrz: null,
    alanGuvenleri: guvenler,
    uyarilar: [],
  };
}
